import { USER_ACTIONS } from './constants';
import type { UserState, AnalyticsTracked, HolidayPromoFlow, HolidayPromoState } from './types';

export const defaultInitialState: UserState = {
    cid: null,
    gcid: null,
    email: null,
    name: null,
    user_age_range: null,
    user_gender: null,
    user_goal: null,
    email_linked: null,
    user_order_id: null,
    social_provider: null,
    social_profile_email: null,
    analytics_tracked: {
        eventsTracked: false,
        lastTrackedValues: {
            cid: null,
            email: null,
            name: null,
        },
    },
    payment_back_path: null,
    device_id: null,
};

export type UserAction =
    | { type: typeof USER_ACTIONS.SET_CID; payload: string }
    | { type: typeof USER_ACTIONS.SET_GCID; payload: string }
    | { type: typeof USER_ACTIONS.SET_EMAIL; payload: string }
    | { type: typeof USER_ACTIONS.SET_NAME; payload: string }
    | { type: typeof USER_ACTIONS.SET_USER_AGE_RANGE; payload: string }
    | { type: typeof USER_ACTIONS.SET_USER_GENDER; payload: string }
    | { type: typeof USER_ACTIONS.SET_USER_GOAL; payload: string }
    | { type: typeof USER_ACTIONS.SET_LINKED_EMAIL; payload: boolean }
    | { type: typeof USER_ACTIONS.SET_USER_ORDER_ID; payload: string }
    | { type: typeof USER_ACTIONS.SET_SOCIAL_PROVIDER; payload: string }
    | { type: typeof USER_ACTIONS.SET_SOCIAL_PROFILE_EMAIL; payload: string }
    | { type: typeof USER_ACTIONS.SET_ANALYTICS_TRACKED; payload: AnalyticsTracked }
    | { type: typeof USER_ACTIONS.SET_PAYMENT_BACK_PATH; payload: string }
    | { type: typeof USER_ACTIONS.SET_DEVICE_ID; payload: string }
    | { type: typeof USER_ACTIONS.SET_HOLIDAY_PROMO_STATE; payload: { flow: HolidayPromoFlow; patch: Partial<HolidayPromoState> } }
    | { type: typeof USER_ACTIONS.SET_MULTIPLE; payload: Partial<UserState> };

const withoutHolidayPromo = (state: UserState): UserState => {
    const { holiday_promo, ...rest } = state;
    return rest;
};

export const userDataReducer = (state: UserState, action: UserAction): UserState => {
    switch (action.type) {
        case USER_ACTIONS.SET_CID:
            return {
                ...(state.cid && state.cid !== action.payload ? withoutHolidayPromo(state) : state),
                cid: action.payload,
            };

        case USER_ACTIONS.SET_GCID:
            return {
                ...state,
                gcid: action.payload,
            };

        case USER_ACTIONS.SET_EMAIL:
            return {
                ...state,
                email: action.payload,
            };

        case USER_ACTIONS.SET_NAME:
            return {
                ...state,
                name: action.payload,
            };

        case USER_ACTIONS.SET_USER_AGE_RANGE:
            return {
                ...state,
                user_age_range: action.payload,
            };

        case USER_ACTIONS.SET_USER_GENDER:
            return {
                ...state,
                user_gender: action.payload,
            };

        case USER_ACTIONS.SET_USER_GOAL:
            return {
                ...state,
                user_goal: action.payload,
            };

        case USER_ACTIONS.SET_LINKED_EMAIL:
            return {
                ...state,
                email_linked: action.payload,
            };

        case USER_ACTIONS.SET_USER_ORDER_ID:
            return {
                ...state,
                user_order_id: action.payload,
            };

        case USER_ACTIONS.SET_SOCIAL_PROVIDER:
            return {
                ...state,
                social_provider: action.payload,
            };

        case USER_ACTIONS.SET_SOCIAL_PROFILE_EMAIL:
            return {
                ...state,
                social_profile_email: action.payload,
            };

        case USER_ACTIONS.SET_ANALYTICS_TRACKED:
            return {
                ...state,
                analytics_tracked: action.payload,
            };

        case USER_ACTIONS.SET_PAYMENT_BACK_PATH:
            return {
                ...state,
                payment_back_path: action.payload,
            };

        case USER_ACTIONS.SET_DEVICE_ID:
            return {
                ...state,
                device_id: action.payload,
            };

        case USER_ACTIONS.SET_HOLIDAY_PROMO_STATE: {
            const { flow, patch } = action.payload;
            const current = state.holiday_promo?.[flow];
            const next: HolidayPromoState = {
                product_scope: 'discount',
                declined: false,
                ...current,
                ...patch,
            };

            if (current?.product_scope === next.product_scope && current.declined === next.declined) {
                return state;
            }

            return {
                ...state,
                holiday_promo: { ...state.holiday_promo, [flow]: next },
            };
        }

        case USER_ACTIONS.SET_MULTIPLE:
            return {
                ...(state.cid && action.payload.cid && state.cid !== action.payload.cid && !action.payload.holiday_promo
                    ? withoutHolidayPromo(state)
                    : state),
                ...action.payload,
            };

        default:
            return state;
    }
};
